import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { auditService, type AuditLog, type AuditSearchFilters } from "@/shared/services/auditService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { formatDateTime } from "@/shared/utils/formatters";

const OFFICER_TYPES = ["officer", "admin"];

type SortKey = "changed_at" | "record_id" | "field_name" | "changed_by_name" | "operation";
type SortDir = "asc" | "desc";

const SortArrow = ({
  column,
  sortKey,
  sortDir,
}: {
  column: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
}) => {
  if (sortKey !== column) return <span className="ml-1 text-gray-300">&#8597;</span>;
  return <span className="ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>;
};

export const InquiryAuditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  // Filters
  const [filterField, setFilterField] = useState("");
  const [filterChangedBy, setFilterChangedBy] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>("changed_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Redirect non-officers
  useEffect(() => {
    if (userData && !isOfficer) {
      navigate("/inquiries", { replace: true });
    }
  }, [userData, isOfficer, navigate]);

  const loadLogs = useCallback(async () => {
    if (!isOfficer) return;
    try {
      setLoading(true);
      const filters: AuditSearchFilters = {
        source_table: "inquiry_tbl",
        limit: 500,
      };
      if (filterField) filters.field_name = filterField;
      if (filterChangedBy) filters.changed_by = filterChangedBy;
      if (filterUserId) filters.user_id = filterUserId;
      if (filterDateFrom) filters.date_from = filterDateFrom;
      if (filterDateTo) filters.date_to = `${filterDateTo}T23:59:59`;

      const data = await auditService.search(filters);
      setLogs(data);
    } catch (err) {
      console.error("Failed to load inquiry audit log:", err);
    } finally {
      setLoading(false);
    }
  }, [isOfficer, filterField, filterChangedBy, filterUserId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "changed_at" ? "desc" : "asc");
    }
  };

  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (sortKey) {
        case "changed_at":
          aVal = a.changed_at || "";
          bVal = b.changed_at || "";
          break;
        case "record_id":
          aVal = a.record_id;
          bVal = b.record_id;
          break;
        case "field_name":
          aVal = a.field_name || "";
          bVal = b.field_name || "";
          break;
        case "changed_by_name":
          aVal = a.changed_by_name || "";
          bVal = b.changed_by_name || "";
          break;
        case "operation":
          aVal = a.operation || "";
          bVal = b.operation || "";
          break;
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const sa = String(aVal).toLowerCase();
      const sb = String(bVal).toLowerCase();
      if (sa < sb) return sortDir === "asc" ? -1 : 1;
      if (sa > sb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [logs, sortKey, sortDir]);

  const clearFilters = () => {
    setFilterField("");
    setFilterChangedBy("");
    setFilterUserId("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const hasActiveFilters = !!(filterField || filterChangedBy || filterUserId || filterDateFrom || filterDateTo);

  const getOperationBadge = (op: string) => {
    if (op === "INSERT") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          {t("audit.operation.INSERT")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
        {t("audit.operation.UPDATE")}
      </span>
    );
  };

  const exportCSV = () => {
    const headers = [
      t("audit.table.changed_at"),
      t("audit.table.record_id"),
      t("audit.table.changed_by"),
      t("audit.table.field"),
      t("audit.table.operation"),
      t("audit.table.old_value"),
      t("audit.table.new_value"),
    ];

    const escape = (v: string) =>
      v.includes(",") || v.includes('"') || v.includes("\n")
        ? `"${v.replace(/"/g, '""')}"`
        : v;

    const rows = sortedLogs.map((log) => [
      escape(log.changed_at ? formatDateTime(log.changed_at) : ""),
      String(log.record_id),
      escape(log.changed_by_name || log.changed_by || ""),
      escape(log.field_name || ""),
      escape(log.operation || ""),
      escape(log.old_value || ""),
      escape(log.new_value || ""),
    ]);

    const csv = [headers.map(escape).join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `inquiry_audit_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Derive unique field names for the filter dropdown
  const fieldOptions = useMemo(() => {
    const seen = new Set<string>();
    logs.forEach((l) => seen.add(l.field_name));
    return Array.from(seen).sort();
  }, [logs]);

  if (!isOfficer) return null;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between md:mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inquiries")}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t("common.back")}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {t("audit.inquiry_title")}
            </h1>
            {!loading && (
              <p className="text-sm text-gray-500 mt-0.5">
                {logs.length} {t("audit.records_found")}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={exportCSV} className="flex-1 md:flex-none">
            {t("inquiry.table.export_csv")}
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <div className="mb-4">
        <button
          onClick={() => setFilterOpen((o) => !o)}
          className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 active:text-indigo-700 transition-colors"
        >
          <svg
            className={`w-4 h-4 transition-transform ${filterOpen ? "rotate-90" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {t("audit.filter.title")}
          {hasActiveFilters && (
            <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-indigo-500 rounded-full">
              ●
            </span>
          )}
        </button>

        {filterOpen && (
          <div className="mt-3 p-4 bg-white rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Field Name */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("audit.filter.field_name")}
                </label>
                <select
                  value={filterField}
                  onChange={(e) => setFilterField(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">{t("common.select")}</option>
                  {fieldOptions.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              {/* Changed By (name search) */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("audit.filter.changed_by")}
                </label>
                <input
                  type="text"
                  value={filterChangedBy}
                  onChange={(e) => setFilterChangedBy(e.target.value)}
                  placeholder={t("audit.filter.changed_by_placeholder")}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Date From */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("audit.filter.date_from")}
                </label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("audit.filter.date_to")}
                </label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {t("common.clear_all")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <Spinner />
      ) : sortedLogs.length === 0 ? (
        <EmptyState title={t("audit.no_results")} />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("changed_at")}
                >
                  {t("audit.table.changed_at")}{" "}
                  <SortArrow column="changed_at" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("record_id")}
                >
                  {t("audit.table.record_id")}{" "}
                  <SortArrow column="record_id" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("changed_by_name")}
                >
                  {t("audit.table.changed_by")}{" "}
                  <SortArrow column="changed_by_name" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("field_name")}
                >
                  {t("audit.table.field")}{" "}
                  <SortArrow column="field_name" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("operation")}
                >
                  {t("audit.table.operation")}{" "}
                  <SortArrow column="operation" sortKey={sortKey} sortDir={sortDir} />
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("audit.table.old_value")}
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("audit.table.new_value")}
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("audit.table.ticket_link")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedLogs.map((log) => (
                <tr key={log.audit_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                    {log.changed_at ? formatDateTime(log.changed_at) : "-"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    #{log.record_id}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap font-medium">
                    {log.changed_by_name || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-gray-100 text-gray-700">
                      {log.field_name}
                    </span>
                  </td>
                  <td className="px-4 py-3">{getOperationBadge(log.operation)}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-[180px]">
                    {log.old_value !== null && log.old_value !== undefined ? (
                      <span className="line-clamp-2 text-red-600 bg-red-50 rounded px-1.5 py-0.5 text-xs">
                        {log.old_value || t("audit.empty_value")}
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-[180px]">
                    {log.new_value !== null && log.new_value !== undefined ? (
                      <span className="line-clamp-2 text-green-700 bg-green-50 rounded px-1.5 py-0.5 text-xs">
                        {log.new_value || t("audit.empty_value")}
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/inquiries/${log.record_id}`)}
                      className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline whitespace-nowrap"
                    >
                      #{log.record_id} →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
